/**
 * IncidentReporter — Generic Behaviour / Incident reporter usable across
 * School, Events, Church, Camps, Conferences, Security, Accommodation,
 * Dining and Transport.
 *
 * Workflow:
 *   1. Open dialog
 *   2. Scan a badge OR enter ID manually  → person profile resolved
 *   3. Pick category + urgency, fill description / location / action
 *   4. Submit → POST /api/incidents → dialog closes, parent refreshes
 *
 * Props:
 *   contextType   "school" | "event" | "church" | ...
 *   contextId     tenant_id / event_id / church_id (optional)
 *   open, onOpenChange, onCreated   (controlled dialog)
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import UnifiedScanInput from '@/components/scan/UnifiedScanInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'disobedience', label: 'Disobedience' },
  { id: 'bullying', label: 'Bullying' },
  { id: 'fighting', label: 'Fighting' },
  { id: 'disrespect', label: 'Disrespect' },
  { id: 'lateness', label: 'Lateness' },
  { id: 'absenteeism', label: 'Absenteeism' },
  { id: 'cheating', label: 'Cheating / Academic dishonesty' },
  { id: 'property_damage', label: 'Property damage' },
  { id: 'inappropriate_language', label: 'Inappropriate language' },
  { id: 'safety_concern', label: 'Safety concern' },
  { id: 'leaving_without_permission', label: 'Leaving area without permission' },
  { id: 'hostel_misconduct', label: 'Hostel / accommodation misconduct' },
  { id: 'transport_misconduct', label: 'Transport misconduct' },
  { id: 'dining_misconduct', label: 'Dining misconduct' },
  { id: 'other', label: 'Other' },
];

const URGENCY = [
  { id: 'yellow', label: 'Yellow — can wait',     swatch: 'bg-yellow-400' },
  { id: 'amber',  label: 'Amber — soon',          swatch: 'bg-amber-500'  },
  { id: 'red',    label: 'Red — IMMEDIATE',       swatch: 'bg-rose-600'   },
];

const initial = {
  category: 'disobedience',
  urgency: 'yellow',
  description: '',
  location: '',
  action_taken: '',
  follow_up_required: false,
};

export default function IncidentReporter({ open, onOpenChange, contextType = 'school', contextId, onCreated }) {
  const [identified, setIdentified] = useState(null);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setIdentified(null); setForm(initial); };
  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange?.(isOpen);
  };

  const submit = async () => {
    if (!identified) { toast.error('Please scan or identify a person first'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          person_id: identified.profile.id,
          person_kind: identified.profile.kind,
          person_name: identified.profile.name,
          context_type: contextType,
          context_id: contextId || identified.profile.tenant_id || identified.profile.event_id || null,
          category: form.category,
          urgency: form.urgency,
          description: form.description,
          location: form.location,
          action_taken: form.action_taken,
          follow_up_required: form.follow_up_required,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Submit failed (${res.status})`);

      if (data.escalated) {
        toast.success('Red-urgency incident escalated to leadership', { duration: 6000 });
      } else {
        toast.success('Incident report submitted');
      }
      onCreated?.(data);
      handleClose(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="incident-reporter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            New behaviour / incident report
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 1. Scan / identify */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">1 · Identify the person</Label>
            <div className="mt-2">
              <UnifiedScanInput
                onIdentified={setIdentified}
                contextId={contextId}
              />
            </div>
          </div>

          {identified && (
            <>
              {/* 2. Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="incident-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                    <SelectTrigger data-testid="incident-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {URGENCY.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${u.swatch}`} />
                            {u.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Location (optional)</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. Class 5A, Hostel B, Bus 2"
                  data-testid="incident-location"
                />
              </div>

              <div>
                <Label>What happened? *</Label>
                <Textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe the incident in factual detail."
                  data-testid="incident-description"
                />
              </div>

              <div>
                <Label>Action already taken (optional)</Label>
                <Input
                  value={form.action_taken}
                  onChange={(e) => setForm({ ...form, action_taken: e.target.value })}
                  placeholder="e.g. spoke to the student, moved seat, called parent"
                  data-testid="incident-action"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.follow_up_required}
                  onChange={(e) => setForm({ ...form, follow_up_required: e.target.checked })}
                  data-testid="incident-followup"
                />
                <span>Follow-up required</span>
              </label>

              {form.urgency === 'red' && (
                <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2 items-start">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>Red urgency</strong> will be escalated to leadership immediately and trigger a notification to the safeguarding chain.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !identified}
            data-testid="incident-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
